import React, { useState, useEffect, useMemo, useRef } from 'react';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode'; 
import LZString from 'lz-string';
import { 
  Upload, Table, RotateCcw, Search, UserCheck, 
  Lock, Wrench, X, Share2, ScanLine, AlertTriangle, Plus, Trash2, Download, User, ArrowRight, Camera, Image as ImageIcon
} from 'lucide-react';

const SeatingApp = () => {
  // --- STATE ---
  const [people, setPeople] = useState([]);
  const [tableSchema, setTableSchema] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [assignedTable, setAssignedTable] = useState(null);
  const [view, setView] = useState('guest'); 
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPerson, setEditingPerson] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [adminUnlock, setAdminUnlock] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  
  // Admin Helper State
  const [tempTableCount, setTempTableCount] = useState(1);
  const [tempSeats, setTempSeats] = useState(8);

  // REFS
  const html5QrCodeRef = useRef(null);
  const scanLock = useRef(false);

  // --- PERSISTENCE ---
  useEffect(() => {
    const savedPeople = localStorage.getItem('event_people');
    const savedSchema = localStorage.getItem('event_schema');
    if (savedPeople) setPeople(JSON.parse(savedPeople));
    if (savedSchema) setTableSchema(JSON.parse(savedSchema));
  }, []);

  useEffect(() => {
    localStorage.setItem('event_people', JSON.stringify(people));
    localStorage.setItem('event_schema', JSON.stringify(tableSchema));
  }, [people, tableSchema]);

  // --- SYNC LOGIC ---
  const generateSyncCode = () => {
    try {
      const data = { 
        p: people.map(person => [person.name, person.table]), 
        c: tableSchema.map(t => [t.id, t.capacity])
      };
      return LZString.compressToBase64(JSON.stringify(data));
    } catch (e) { return "error"; }
  };

  const handleDecodedText = (decodedText) => {
    if (scanLock.current) return;
    scanLock.current = true;

    try {
      const decompressed = LZString.decompressFromBase64(decodedText);
      const jsonString = decompressed || decodedText;
      const decoded = JSON.parse(jsonString);

      if (html5QrCodeRef.current) {
         try { html5QrCodeRef.current.pause(); } catch(e) {}
      }

      if (window.confirm("Found data! Overwrite current system?")) {
        let expandedPeople = [];
        let expandedSchema = [];

        if (Array.isArray(decoded.p[0])) {
           expandedPeople = decoded.p.map(item => ({ name: item[0], table: item[1] }));
           expandedSchema = decoded.c.map(item => ({ id: item[0], capacity: item[1] }));
        } else {
           expandedPeople = decoded.p;
           expandedSchema = decoded.c;
        }

        setPeople(expandedPeople);
        setTableSchema(expandedSchema);
        
        stopScanner().then(() => setShowSyncModal(false));
      } else {
        stopScanner().then(() => setShowSyncModal(false));
      }
    } catch (e) { 
        console.log("Invalid QR Data", e);
        alert("Invalid QR Code format.");
        stopScanner().then(() => setShowSyncModal(false));
    }
  };

  // --- ROBUST CAMERA LOGIC ---
  const startCamera = () => {
    setIsScanning(true);
    scanLock.current = false; 
    
    setTimeout(() => {
        if(!html5QrCodeRef.current) {
            html5QrCodeRef.current = new Html5Qrcode("reader");
        }
        
        const config = { fps: 10, qrbox: { width: 250, height: 250 } };
        
        html5QrCodeRef.current.start(
            { facingMode: "environment" }, 
            config, 
            handleDecodedText,
            (errorMessage) => { /* ignore per-frame errors */ }
        ).catch(err => {
            console.log("Camera failed", err);
            setIsScanning(false);
            alert("Camera failed to start. Please check permissions.");
        });
    }, 100);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn("Stop failed", err);
      }
      setIsScanning(false);
      scanLock.current = false;
    }
  };

  const safeCloseModal = () => {
      stopScanner();
      setShowSyncModal(false);
  };

  // --- FILE SCAN LOGIC ---
  const handleScanFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const html5QrCode = new Html5Qrcode("reader"); 
    
    html5QrCode.scanFile(file, true)
        .then(decodedText => {
            handleDecodedText(decodedText);
        })
        .catch(err => {
            alert("Could not find a QR code in this image. Try a clearer photo.");
        });
  };

  // --- APP LOGIC ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const newList = results.data.map(row => ({
          name: row.Name || row.name || Object.values(row)[0],
          table: null
        })).filter(p => p.name);
        setPeople(newList);
        alert(`Imported ${newList.length} guests.`);
      }
    });
  };

  const downloadReport = () => {
    const csv = Papa.unparse(people);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "final_seating_report.csv");
    link.click();
  };

  const addTableGroup = () => {
    const newTables = [];
    const startId = tableSchema.length > 0 ? Math.max(...tableSchema.map(t => t.id)) + 1 : 1;
    for (let i = 0; i < tempTableCount; i++) {
      newTables.push({ id: startId + i, capacity: tempSeats });
    }
    setTableSchema([...tableSchema, ...newTables]);
  };

  const forceAssign = (name, tableId) => {
    const tableObj = tableSchema.find(t => t.id === parseInt(tableId));
    if (!tableObj) return alert("Table ID does not exist.");
    setPeople(prev => prev.map(p => p.name === name ? { ...p, table: tableObj.id } : p));
    setEditingPerson(null);
  };

  const handleMasterReset = () => {
    if (window.confirm("RESET SYSTEM? This deletes all guests and tables.") && 
        window.confirm("FINAL WARNING: All data will be lost. Confirm?")) {
      stopScanner();
      setPeople([]);
      setTableSchema([]);
      localStorage.clear();
      alert("System Reset Complete.");
    }
  };

  const handleRandomAssign = () => {
    if (!selectedName || tableSchema.length === 0) return;
    const person = people.find(p => p.name === selectedName);
    if (person?.table) { setAssignedTable(person.table); return; }

    const occupancy = {};
    people.forEach(p => { if (p.table) occupancy[p.table] = (occupancy[p.table] || 0) + 1; });
    const available = tableSchema.filter(t => (occupancy[t.id] || 0) < t.capacity);

    if (available.length === 0) return alert("All tables are full!");
    const randomTable = available[Math.floor(Math.random() * available.length)];
    setPeople(prev => prev.map(p => p.name === selectedName ? { ...p, table: randomTable.id } : p));
    setAssignedTable(randomTable.id);
  };

  const statsByCapacity = useMemo(() => {
    const counts = {};
    const occupancy = {};
    people.forEach(p => { if (p.table) occupancy[p.table] = (occupancy[p.table] || 0) + 1; });
    tableSchema.forEach(t => {
      const cap = t.capacity;
      if (!counts[cap]) counts[cap] = { total: 0, taken: 0, tables: 0 };
      counts[cap].tables += 1;
      counts[cap].total += cap;
      counts[cap].taken += (occupancy[t.id] || 0);
    });
    return counts;
  }, [people, tableSchema]);

  const filteredPeople = useMemo(() => 
    people.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [people, searchTerm]
  );

  return (
    <div className="min-h-screen bg-[#0f1115] text-gray-100 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          
          {/* LOGO SECTION */}
          <div className="flex items-center gap-3">
             <img src="/logo192.png" alt="Logo" className="w-10 h-10 rounded-full shadow-lg shadow-[#cf1e35]/20 border border-gray-800" />
             <span className="text-[#cf1e35] font-black uppercase tracking-tighter text-2xl hidden md:block">SRandomizer</span>
          </div>

          {/* VIEW SWITCHER */}
          <div className="flex items-center gap-3">
             <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${view === 'guest' ? 'text-white' : 'text-gray-600'}`}>Guest</span>
             
             <div 
               onClick={() => {
                 setView(view === 'guest' ? 'admin' : 'guest');
                 setAssignedTable(null); 
                 setSelectedName(""); 
                 setSearchTerm("");
               }}
               className="w-14 h-7 bg-[#1a1d23] border border-gray-700 rounded-full relative cursor-pointer shadow-inner transition-colors hover:border-gray-500"
             >
                <div className={`absolute top-1 w-5 h-5 bg-[#cf1e35] rounded-full shadow-md transition-all duration-300 ${view === 'admin' ? 'left-8' : 'left-1'}`} />
             </div>

             <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${view === 'admin' ? 'text-white' : 'text-gray-600'}`}>Admin</span>
          </div>
        </div>

        {view === 'admin' ? (
          <div className="grid lg:grid-cols-4 gap-6 items-start">
            <div className="space-y-6 lg:col-span-1">
              {/* Stats */}
              <div className="bg-[#1a1d23] p-5 rounded-3xl border border-gray-800 shadow-xl">
                <h3 className="font-bold mb-4 text-[#cf1e35] text-[10px] uppercase tracking-widest">Real-Time Occupancy</h3>
                <div className="space-y-3">
                  {Object.entries(statsByCapacity).map(([cap, data]) => (
                    <div key={cap} className="bg-[#0f1115] p-3 rounded-xl border border-gray-800">
                      <div className="flex justify-between text-[11px] mb-1 font-bold">
                        <span>{cap} Seaters</span>
                        <span className={data.taken >= data.total ? "text-[#cf1e35]" : "text-emerald-500"}>{data.taken}/{data.total}</span>
                      </div>
                      <div className="w-full bg-gray-900 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-[#cf1e35] h-full transition-all duration-500" style={{ width: `${(data.taken / data.total) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Table Config */}
              <div className="bg-[#1a1d23] p-5 rounded-3xl border border-gray-800 shadow-xl">
                <h3 className="font-bold mb-4 text-gray-400 text-[10px] uppercase tracking-widest">Configure Tables</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <input type="number" value={tempTableCount} onChange={e => setTempTableCount(parseInt(e.target.value))} className="w-full bg-[#0f1115] border border-gray-700 rounded-lg p-3 text-white text-sm" />
                  <input type="number" value={tempSeats} onChange={e => setTempSeats(parseInt(e.target.value))} className="w-full bg-[#0f1115] border border-gray-700 rounded-lg p-3 text-white text-sm" />
                </div>
                <button onClick={addTableGroup} className="w-full py-4 bg-gray-800 hover:bg-gray-700 rounded-xl text-xs font-black uppercase transition mb-4">Add Group</button>
                <div className="max-h-64 overflow-y-auto border-t border-gray-800 pt-3 space-y-2">
                   {tableSchema.map(t => (
                     <div key={t.id} className="flex justify-between items-center bg-[#0f1115] p-3 rounded-xl border border-gray-800">
                        <span className="text-sm font-bold text-gray-300">Table {t.id} <span className="text-gray-600 text-xs ml-1">({t.capacity} seats)</span></span>
                        <button onClick={() => setTableSchema(tableSchema.filter(x => x.id !== t.id))} className="bg-red-900/20 hover:bg-red-900/50 p-2 rounded-lg text-red-500 transition"><Trash2 size={16}/></button>
                     </div>
                   ))}
                </div>
              </div>

              {/* Import/Reset */}
              <div className="bg-[#1a1d23] p-5 rounded-3xl border border-gray-800 shadow-xl">
                <h3 className="font-bold mb-4 text-gray-400 text-[10px] uppercase tracking-widest">Import Guests</h3>
                <div className="flex flex-col gap-2">
                  <label className="w-full flex items-center justify-center gap-2 py-3 bg-[#cf1e35]/10 border border-[#cf1e35]/30 rounded-xl cursor-pointer hover:bg-[#cf1e35]/20 transition group">
                    <Upload size={16} className="text-[#cf1e35]" /><span className="text-[10px] font-black text-[#cf1e35] uppercase">Select CSV</span>
                    <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <button onClick={handleMasterReset} className="w-full py-4 bg-red-950/20 hover:bg-red-900/40 text-red-500 rounded-2xl border border-red-900/30 text-[10px] font-black uppercase flex items-center justify-center gap-2 transition tracking-widest"><RotateCcw size={14}/> Full Reset</button>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 bg-[#1a1d23] rounded-[2.5rem] border border-gray-800 shadow-2xl overflow-hidden flex flex-col h-[750px]">
              <div className="p-5 border-b border-gray-800 flex justify-between items-center bg-[#16191f]">
                <div className="flex items-center gap-3 bg-[#0f1115] px-4 py-2.5 rounded-2xl border border-gray-800 w-full max-w-sm">
                  <Search size={18} className="text-gray-500" />
                  <input placeholder="Search guests..." className="bg-transparent outline-none text-sm w-full text-white" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
                <div className="flex gap-3 ml-4">
                   <button onClick={downloadReport} title="Export Report" className="p-2.5 bg-gray-800 rounded-xl text-emerald-500 hover:bg-emerald-500 hover:text-white transition"><Download size={18}/></button>
                   <button onClick={() => setShowSyncModal(true)} title="Sync Devices" className="p-2.5 bg-gray-800 rounded-xl text-indigo-400 hover:bg-indigo-400 hover:text-white transition"><Share2 size={18}/></button>
                   <label className="flex items-center gap-2 cursor-pointer bg-gray-800 px-3 py-1 rounded-xl">
                      <div onClick={() => setAdminUnlock(!adminUnlock)} className={`w-8 h-4 rounded-full relative transition ${adminUnlock ? 'bg-[#cf1e35]' : 'bg-gray-600'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${adminUnlock ? 'left-4.5' : 'left-0.5'}`} /></div>
                   </label>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto scrollbar-hide">
                <table className="w-full text-left">
                  <thead className="bg-[#0f1115] text-[10px] uppercase text-gray-500 font-black sticky top-0 z-10 shadow-lg">
                    <tr>
                      <th className="px-8 py-4">Guest Name</th>
                      <th className="px-8 py-4 text-center">Table ID</th>
                      <th className="px-8 py-4 text-right">Settings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {filteredPeople.map((p, i) => (
                      <tr key={i} className="hover:bg-white/[0.02] transition">
                        <td className="px-8 py-5 font-bold text-gray-300 text-sm italic">{p.name}</td>
                        <td className="px-8 py-5 text-center"><span className={`px-5 py-1.5 rounded-xl font-black text-xs ${p.table ? 'bg-[#cf1e35] text-white shadow-lg shadow-[#cf1e35]/20' : 'bg-gray-800 text-gray-700'}`}>{p.table ? `TABLE ${p.table}` : 'NOT SEATED'}</span></td>
                        <td className="px-8 py-5 text-right"><button disabled={p.table && !adminUnlock} onClick={() => setEditingPerson(p)} className={`p-2 rounded-lg transition ${p.table && !adminUnlock ? 'opacity-0' : 'text-[#cf1e35] hover:bg-white/5'}`}><Wrench size={16}/></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          /* GUEST VIEW */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto mt-6 md:mt-12">
            <div className="bg-[#1a1d23] rounded-[3rem] p-8 md:p-16 shadow-2xl text-center border-t-8 border-[#cf1e35] relative overflow-hidden">
              <h2 className="text-4xl font-black mb-10 text-white italic uppercase tracking-tight">Check In</h2>
              
              <div className="space-y-6 text-left">
                {!selectedName ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-5 top-5 text-gray-600" size={24} />
                      <input placeholder="Search your name..." className="w-full pl-14 pr-6 py-5 rounded-2xl border-2 border-gray-800 bg-[#0f1115] text-xl font-bold text-white focus:border-[#cf1e35] outline-none transition shadow-inner" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                    {searchTerm.length >= 2 && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="max-h-64 overflow-y-auto rounded-2xl border-2 border-gray-800 bg-[#0f1115] divide-y divide-gray-800 scrollbar-hide">
                        {filteredPeople.length > 0 ? filteredPeople.map(p => (
                          <button key={p.name} onClick={() => {setSelectedName(p.name); setSearchTerm("");}} className="w-full px-6 py-4 text-left text-lg font-bold hover:bg-[#cf1e35]/10 hover:text-[#cf1e35] transition flex items-center justify-between text-gray-300">
                            {p.name}<User size={18} className="text-gray-700" />
                          </button>
                        )) : (
                          <div className="p-6 text-center text-gray-500 font-bold">No results found</div>
                        )}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="space-y-6">
                    {!assignedTable && (
                      <div className="bg-[#cf1e35]/10 border-2 border-[#cf1e35]/30 rounded-3xl p-6 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-[#cf1e35] p-3 rounded-2xl shadow-lg shadow-[#cf1e35]/20 text-white"><UserCheck size={24} /></div>
                          <div><p className="text-[10px] font-black uppercase text-[#cf1e35] tracking-widest">Selected Guest</p><h3 className="text-2xl font-black text-white">{selectedName}</h3></div>
                        </div>
                        <button onClick={() => {setSelectedName(""); setAssignedTable(null);}} className="text-gray-500 hover:text-white p-2"><X size={24} /></button>
                      </div>
                    )}

                    {!assignedTable ? (
                      <button onClick={handleRandomAssign} className="w-full bg-[#cf1e35] text-white font-black py-7 rounded-[2rem] shadow-2xl shadow-[#cf1e35]/40 text-2xl transition active:scale-95 uppercase tracking-tighter">Find My Table</button>
                    ) : (
                      <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center">
                        <div className="bg-black rounded-[3.5rem] p-12 border-2 border-gray-800 shadow-inner relative overflow-hidden">
                           <div className="absolute top-0 left-0 w-full h-2 bg-[#cf1e35]" />
                           <p className="text-[#cf1e35] text-xs font-black uppercase tracking-[0.5em] mb-4">Table Assigned</p>
                           <div className="text-[140px] leading-none font-black text-white italic tracking-tighter relative z-10">#{assignedTable}</div>
                           <p className="mt-8 text-gray-400 font-bold text-xl">{selectedName}</p>
                        </div>
                        <button onClick={() => {setSelectedName(""); setAssignedTable(null); setSearchTerm("");}} className="mt-10 w-full py-6 bg-gray-800 hover:bg-gray-700 text-white rounded-2xl text-xl font-bold uppercase tracking-widest transition shadow-lg flex items-center justify-center gap-3 active:scale-95">Finish / Next Guest <ArrowRight size={24}/></button>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>
            </div>
            
            {/* POWERED BY FOOTER */}
            <p className="text-center text-gray-600 text-[10px] font-bold uppercase tracking-widest mt-6">Powered by Dimitar Vasilkov</p>
          </motion.div>
        )}

        {/* --- SYNC MODAL --- */}
        {showSyncModal && (
          <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={safeCloseModal}>
            <div className="bg-[#1a1d23] rounded-3xl p-6 md:p-10 max-w-2xl w-full border border-gray-800 my-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between mb-8"><h3 className="font-black text-xl text-white underline decoration-[#cf1e35]">Device Sync</h3><button onClick={safeCloseModal}><X className="text-gray-500"/></button></div>
              <div className="grid md:grid-cols-2 gap-8 text-center">
                <div className="bg-[#0f1115] p-6 rounded-2xl border border-gray-800">
                  <p className="text-[10px] font-black text-gray-500 uppercase mb-4 tracking-widest">Master Display</p>
                  <div className="bg-white p-4 rounded-xl inline-block mb-4">
                    <QRCodeSVG value={generateSyncCode()} size={220} level="L" />
                  </div>
                  <p className="text-[10px] text-gray-500">Scan from new device</p>
                </div>
                <div className="bg-[#0f1115] p-6 rounded-2xl border border-gray-800 flex flex-col justify-center">
                  <p className="text-[10px] font-black text-gray-500 uppercase mb-4 tracking-widest">New Tablet Scanner</p>
                  
                  {!isScanning ? (
                    <div className="space-y-3">
                        <button onClick={startCamera} className="w-full py-6 border-2 border-dashed border-gray-800 rounded-xl text-[#cf1e35] font-bold flex flex-col items-center gap-2 hover:bg-[#cf1e35]/5 transition">
                            <Camera size={24}/>
                            <span>Start Camera</span>
                        </button>
                        <div className="relative">
                            <input type="file" accept="image/*" onChange={handleScanFile} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                            <button className="w-full py-4 bg-gray-800 rounded-xl text-gray-400 font-bold flex items-center justify-center gap-2 hover:text-white transition">
                                <ImageIcon size={20}/> Upload QR Image
                            </button>
                        </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                        <div id="reader" className="w-full rounded-xl overflow-hidden shadow-2xl shadow-black border border-gray-700"></div>
                        <button onClick={stopScanner} className="text-xs font-bold text-red-500 uppercase">Stop Camera</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- EDIT MODAL --- */}
        {editingPerson && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d23] rounded-[3rem] p-12 max-w-sm w-full border border-gray-800 text-center shadow-2xl">
              <h3 className="text-xl font-bold mb-6 text-white uppercase italic tracking-tighter">Override Seat</h3>
              <select id="mTable" className="w-full p-4 bg-[#0f1115] border border-gray-800 rounded-2xl text-xl font-bold mb-10 text-white outline-none appearance-none text-center">
                {tableSchema.map(t => <option key={t.id} value={t.id}>Table {t.id} ({t.capacity} Seats)</option>)}
              </select>
              <div className="flex gap-4">
                <button onClick={() => setEditingPerson(null)} className="flex-1 font-bold text-gray-500 text-xs tracking-widest uppercase">Cancel</button>
                <button onClick={() => forceAssign(editingPerson.name, document.getElementById('mTable').value)} className="flex-1 bg-[#cf1e35] text-white font-black py-4 rounded-2xl text-xs uppercase shadow-lg shadow-[#cf1e35]/20">Update</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SeatingApp;